import { storyblokEditable } from "@storyblok/react";
import { useEffect, useMemo, useRef } from "react";
import DOMPurify from "dompurify";

const TWITTER_WIDGETS_SRC = "https://platform.twitter.com/widgets.js";

// Embeds we actually use: Twitch clip/VOD players, YouTube players,
// self-hosted video, and Twitter/X tweet widgets. Everything else (scripts,
// inline event handlers, arbitrary iframes) is stripped.
const EMBED_ALLOWED_TAGS = ["iframe", "video", "source", "blockquote", "a", "p", "br"];
const EMBED_ALLOWED_ATTR = [
  "src",
  "href",
  "type",
  "controls",
  "frameborder",
  "allowfullscreen",
  "scrolling",
  "class",
  "lang",
  "dir",
  "style",
];
const ALLOWED_IFRAME_HOSTS = ["clips.twitch.tv", "player.twitch.tv"];
// YouTube hosts much more than its player, so only its /embed/ pages.
const YOUTUBE_EMBED_HOSTS = ["www.youtube.com", "youtube.com", "www.youtube-nocookie.com"];

function isAllowedIframeSrc(src) {
  let url;
  try {
    url = new URL(src, window.location.href);
  } catch {
    return false; // unparseable src is treated as disallowed
  }
  if (ALLOWED_IFRAME_HOSTS.includes(url.hostname)) return true;
  return YOUTUBE_EMBED_HOSTS.includes(url.hostname) && url.pathname.startsWith("/embed/");
}

function sanitizeEmbedHtml(html) {
  const clean = DOMPurify.sanitize(html || "", {
    ALLOWED_TAGS: EMBED_ALLOWED_TAGS,
    ALLOWED_ATTR: EMBED_ALLOWED_ATTR,
    ALLOWED_URI_REGEXP: /^(?:https:)?\/\//, // no javascript:/data: URIs
    FORCE_BODY: true,
  });

  // DOMPurify has no per-domain iframe allowlist, so do a second explicit
  // pass: parse the already-script-free output and drop any <iframe> whose
  // src isn't one of the embed players we actually use.
  const doc = new DOMParser().parseFromString(clean, "text/html");
  doc.querySelectorAll("iframe").forEach((iframe) => {
    if (!isAllowedIframeSrc(iframe.getAttribute("src"))) {
      iframe.remove();
      return;
    }
    // Some stored embeds are a bare <iframe src>; give every player
    // working fullscreen/playback and a name for screen readers. Sizing is
    // in blog-post.css (.blog-embed).
    iframe.setAttribute("allowfullscreen", "");
    iframe.setAttribute("allow", "autoplay; encrypted-media; fullscreen; picture-in-picture");
    if (!iframe.getAttribute("title")) iframe.setAttribute("title", "Embedded video");
  });
  return doc.body.innerHTML;
}

function loadTwitterWidgets() {
  if (window.twttr?.widgets) {
    return Promise.resolve(window.twttr);
  }
  if (!document.querySelector(`script[src="${TWITTER_WIDGETS_SRC}"]`)) {
    const script = document.createElement("script");
    script.src = TWITTER_WIDGETS_SRC;
    script.async = true;
    document.body.appendChild(script);
  }
  return new Promise((resolve) => {
    const check = setInterval(() => {
      if (window.twttr?.widgets) {
        clearInterval(check);
        resolve(window.twttr);
      }
    }, 100);
  });
}

const Embed = ({ blok }) => {
  const containerRef = useRef(null);
  const sanitizedHtml = useMemo(() => sanitizeEmbedHtml(blok.embed_html), [blok.embed_html]);
  const isTweet = sanitizedHtml.includes("twitter-tweet");

  useEffect(() => {
    if (!isTweet || !containerRef.current) return;
    loadTwitterWidgets().then((twttr) => {
      twttr.widgets.load(containerRef.current);
    });
  }, [isTweet]);

  return (
    <div
      {...storyblokEditable(blok)}
      ref={containerRef}
      className="blog-embed"
      dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
    />
  );
};

export default Embed;
